import { useState, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AppBar, Box, IconButton, Toolbar, useMediaQuery } from '@mui/material';
import Badge from '@mui/material/Badge';
import { experimentalStyled, useTheme } from '@mui/material/styles';
import MenuIcon from '../../icons/Menu';
import AccountPopover from './AccountPopover';
import ContentSearch from './ContentSearch';
import LogoMobile from '../LogoMobile';
import HeaderLogo from '../HeaderLogo';
import NotificationsPopover from './NotificationsPopover';
import { db } from '../../lib/firebase';
import useAuth from '../../hooks/useAuth';
import ExchangeRatesPopover from './ExchangeRatesPopover';
import UserGuidesRedirect from './UserGuidesRedirect';
import useParametros from '../../hooks/useParameters';
import BackendStatusIndicator from './BackendStatusIndicator';

const sortList = (list) => {
  return list.sort((a, b) => new Date(b.date) - new Date(a.date));
};

const DashboardNavbarRoot = experimentalStyled(AppBar)(({ theme }) => ({
  ...(theme.palette.mode === 'light' && {
    backgroundColor: theme.palette.primary.main,
    boxShadow: 'none',
    color: theme.palette.primary.contrastText,
  }),
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
  }),
  zIndex: theme.zIndex.drawer - 1, // era + 100
}));

const PAGE_SIZE = 100;
const mapDocs = (snapshot) => snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [lastNotificationsFetched, setLastNotificationsFetched] = useState(null);
  // Última foto de la primera página (la única en vivo) para "Volver" sin releer.
  const firstPageRef = useRef({ docs: [], last: null });
  const { user } = useAuth();

  const baseQuery = () =>
    db.collection('users').doc(user.uid).collection('notifications').orderBy('createdAt', 'desc');

  useEffect(() => {
    const unsubRef = baseQuery()
      .limit(PAGE_SIZE)
      .onSnapshot((snapshot) => {
        const docs = mapDocs(snapshot);
        const last = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
        firstPageRef.current = { docs, last };
        setNotifications(docs);
        if (last) setLastNotificationsFetched(last);
      });

    return () => unsubRef();
  }, []);

  // Páginas anteriores: lectura única. Antes cada click abría un onSnapshot que
  // nunca se cerraba (un listener más por click, cada uno con su lectura inicial).
  const fetchMoreNotifications = async () => {
    if (!lastNotificationsFetched) return;
    const snapshot = await baseQuery().startAfter(lastNotificationsFetched).limit(PAGE_SIZE).get();
    if (snapshot.docs.length > 0) {
      setNotifications(mapDocs(snapshot));
      setLastNotificationsFetched(snapshot.docs[snapshot.docs.length - 1]);
    }
  };

  // Volver: la primera página sigue viva en el listener, no hay que releerla.
  const fetchGoBackNotifications = () => {
    setNotifications(firstPageRef.current.docs);
    setLastNotificationsFetched(firstPageRef.current.last);
  };

  // Las páginas anteriores no tienen listener: se marcan leídas en local tras escribir.
  const markReadLocal = (ids) => {
    setNotifications((prev) => prev.map((n) => (ids.includes(n.id) ? { ...n, notificationRead: true } : n)));
  };

  return [notifications, lastNotificationsFetched, { fetchMoreNotifications, fetchGoBackNotifications, markReadLocal }];
};

function DashboardNavbar(props) {
  const { onSidebarMobileOpen, ...other } = props;
  const [notifications, lastNotificationsFetched, { fetchMoreNotifications, fetchGoBackNotifications, markReadLocal }] =
    useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Adjust 'md' breakpoint as needed
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { changelog } = useParametros();
  const changelogDateSort = sortList(changelog ?? []);
  const lastSeenUpgrade = localStorage.getItem('badgeVisible');

  // console.log(notifications);
  // console.log(user);
  const handleClick = (id) => {
    setOpen(false);
    db.collection('users')
      .doc(user.uid)
      .collection('notifications')
      .doc(id)
      .update({ notificationRead: true }, (error) => {
        if (error) {
          console.log({ msg: 'Error setting read false to notification', user: user.uid, notification: id });
        }
      });
    markReadLocal([id]);
  };

  // Un solo batch con las no leídas de la página cargada (máx. 100, bajo el tope de 500).
  const handleClickMultipleMarkRead = () => {
    setOpen(false);
    const pendientes = notifications.filter((doc) => doc.notificationRead === false);
    if (pendientes.length === 0) return;
    const batch = db.batch();
    pendientes.forEach((doc) => {
      batch.update(db.collection('users').doc(user.uid).collection('notifications').doc(doc.id), {
        notificationRead: true,
      });
    });
    batch.commit().catch((error) => {
      console.log({ msg: 'Error marking notifications as read', user: user.uid, error });
    });
    markReadLocal(pendientes.map((doc) => doc.id));
  };

  return (
    <DashboardNavbarRoot {...other}>
      <Toolbar sx={{ minHeight: 64 }}>
        {/* <IconButton change
          color="inherit"
          onClick={onSidebarMobileOpen}
          sx={{
            display: {
              lg: 'none'
            }
          }}
          size="large"
        > */}
        <IconButton color="inherit" onClick={onSidebarMobileOpen} size="large">
          <Badge
            variant="dot"
            invisible={!changelogDateSort.length || lastSeenUpgrade === changelogDateSort[0].date}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ '& .MuiBadge-badge': { backgroundColor: theme.palette.error.main } }}
          >
            <MenuIcon fontSize="small" />
          </Badge>
        </IconButton>
        <RouterLink to="/panel">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center', // This will vertically center the logo
              height: '100%', // Take the full height of the toolbar
            }}
          >
            {isMobile ? (
              <LogoMobile sx={{ display: { xs: 'none' }, height: 20, width: 40 }} />
            ) : (
              <HeaderLogo height={36} variant="white" />
            )}
          </Box>
        </RouterLink>
        <Box sx={{ flexGrow: 1, ml: 2 }} />
        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
          <BackendStatusIndicator />
        </Box>
        <Box sx={{ ml: 1 }}>
          <ContentSearch />
        </Box>
        <Box>
          <UserGuidesRedirect />
        </Box>
        <Box>
          <ExchangeRatesPopover />
        </Box>
        <Box sx={{ ml: 1 }}>
          <NotificationsPopover
            handleClick={handleClick}
            handleClickMultipleMarkRead={handleClickMultipleMarkRead}
            notifications={notifications}
            lastNotificationsFetched={lastNotificationsFetched}
            fetchMoreNotifications={fetchMoreNotifications}
            fetchGoBackNotifications={fetchGoBackNotifications}
            open={open}
            setOpen={setOpen}
          />
        </Box>
        <Box sx={{ ml: 2 }}>
          <AccountPopover />
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
}

DashboardNavbar.propTypes = { onSidebarMobileOpen: PropTypes.func };

export default DashboardNavbar;
